import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  IconButton,
  Typography,
  Box
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PhotoFrameIcon from '@mui/icons-material/CropOriginal';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { postData, updateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';

const FrameModal = ({
  open,
  onClose,
  onSave,
  FrameData = null,
  mode = 'create',
  existingFrames = []
}) => {
  const { companyId, userId, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);

  const [formData, setFormData] = useState({
    Name: '',
    Rate: '',
    Thickness: '',
    FrameImagelocation: '',
  });

  const [errors, setErrors] = useState({});
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicateMessage, setDuplicateMessage] = useState('');

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (open) {
      if (FrameData && mode !== 'create') {
        setFormData({
          Id: FrameData.Id || '',
          Name: FrameData.Name || '',
          Rate: FrameData.Rate ?? '',
          Thickness: FrameData.Thickness ?? '',
          FrameImagelocation: FrameData.FrameImagelocation || '',
        });
      } else {
        setFormData({ Name: '', Rate: '', Thickness: '', FrameImagelocation: '' });
      }
      setErrors({});
    }
  }, [open, FrameData, mode]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.Name.trim()) newErrors.Name = 'Frame name is required';
    if (formData.Rate === '' || formData.Rate === null || formData.Rate === undefined) {
      newErrors.Rate = 'Rate is required';
    } else if (isNaN(formData.Rate) || parseFloat(formData.Rate) < 0) {
      newErrors.Rate = 'Enter a valid rate';
    }
    if (formData.Thickness === '' || formData.Thickness === null || formData.Thickness === undefined) {
      newErrors.Thickness = 'Thickness is required';
    } else if (isNaN(formData.Thickness) || parseFloat(formData.Thickness) < 0) {
      newErrors.Thickness = 'Enter a valid thickness';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const checkDuplicate = () => {
    return existingFrames.find(frame => {
      if (mode === 'edit' && FrameData && frame.Id === FrameData.Id) return false;
      return frame.Name?.trim().toLowerCase() === formData.Name.trim().toLowerCase();
    });
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    const duplicate = checkDuplicate();
    if (duplicate) {
      setDuplicateMessage(`A Frame with the name "${formData.Name}" already exists.`);
      setDuplicateDialogOpen(true);
      return;
    }

    setLoading(true);
    try {
      const dataToSend = {
        Name: formData.Name,
        Rate: parseFloat(formData.Rate) || 0,
        Thickness: parseFloat(formData.Thickness) || 0,
        FrameImagelocation: formData.FrameImagelocation || '',
        CompanyId: companyId,
      };

      if (mode === 'edit') {
        if (!FrameData || !FrameData.Id) {
          alert('Error: Cannot update Frame - missing ID');
          return;
        }
        dataToSend.Id = FrameData.Id;
        dataToSend.ModifiedBy = userId;
        dataToSend.ModifiedDate = new Date();
        if (FrameData.CreatedBy) dataToSend.CreatedBy = FrameData.CreatedBy;
        if (FrameData.CreatedDate) dataToSend.CreatedDate = FrameData.CreatedDate;
        await updateData('FrameApi', FrameData.Id, dataToSend, token);
      } else {
        dataToSend.CreatedBy = userId;
        dataToSend.CreatedDate = new Date();
        await postData('FrameApi','', dataToSend, token);
      }

      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving Frame:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTitle = () => {
    switch (mode) {
      case 'create': return 'New Frame';
      case 'edit': return 'Edit Frame';
      case 'view': return 'Frame Details';
      default: return 'Frame';
    }
  };

  const isReadOnly = mode === 'view';
  const inputStyle = { borderRadius: '6px', fontSize: '13px', height: '32px' };
  const labelStyle = { fontSize: '11px' };
  const helperStyle = { fontSize: '9px', margin: '1px 0 0 0' };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          style: {
            borderRadius: isMobile ? '0' : '12px',
            background: 'linear-gradient(135deg, #3b2410 0%, #5c3d1e 100%)',
            padding: '2px',
            margin: isMobile ? '0' : '8px'
          }
        }}
      >
        <Box style={{
          background: 'white',
          borderRadius: isMobile ? '0' : '10px',
          overflow: 'hidden',
          height: isMobile ? '100vh' : 'auto'
        }}>
          <DialogTitle style={{
            background: 'linear-gradient(135deg, #3b2410 0%, #5c3d1e 100%)',
            color: 'white',
            padding: '8px 12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            minHeight: 'auto'
          }}>
            <Box style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <PhotoFrameIcon style={{ fontSize: '18px' }} />
              <Typography variant="h6" style={{ fontWeight: '600', fontSize: '14px' }}>
                {getTitle()}
              </Typography>
            </Box>
            <IconButton onClick={onClose} style={{ color: 'white', padding: '2px' }} size="small">
              <CloseIcon style={{ fontSize: '18px' }} />
            </IconButton>
          </DialogTitle>

          <DialogContent style={{ padding: '8px 12px', paddingBottom: '4px' }}>
            <Typography variant="subtitle2" style={{
              color: '#666', marginBottom: '6px', fontSize: '10px', lineHeight: '1.2'
            }}>
              {mode === 'create' ? 'Add a new Frame to your system' :
                mode === 'edit' ? 'Update Frame information' : 'View Frame details'}
            </Typography>

            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Typography variant="subtitle1" style={{
                  fontWeight: '600', marginBottom: '3px', color: '#2d3748', fontSize: '12px'
                }}>
                  Frame Information
                </Typography>
              </Grid>

              {/* Name */}
              <Grid item xs={12}>
                <TextField
                  name="Name"
                  label="Frame Name *"
                  value={formData.Name}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  error={!!errors.Name}
                  helperText={errors.Name}
                  disabled={isReadOnly}
                  placeholder="Enter Frame name"
                  inputProps={{ maxLength: 100 }}
                  InputProps={{ style: inputStyle }}
                  InputLabelProps={{ style: labelStyle }}
                  FormHelperTextProps={{ style: helperStyle }}
                />
              </Grid>

              {/* Rate & Thickness */}
              <Grid item xs={6}>
                <TextField
                  name="Rate"
                  label="Rate (₹) *"
                  value={formData.Rate}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  type="number"
                  error={!!errors.Rate}
                  helperText={errors.Rate}
                  disabled={isReadOnly}
                  placeholder="0.00"
                  inputProps={{ min: 0, step: '0.01' }}
                  InputProps={{ style: inputStyle }}
                  InputLabelProps={{ style: labelStyle }}
                  FormHelperTextProps={{ style: helperStyle }}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  name="Thickness"
                  label="Thickness *"
                  value={formData.Thickness}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  type="number"
                  error={!!errors.Thickness}
                  helperText={errors.Thickness}
                  disabled={isReadOnly}
                  placeholder="0.00"
                  inputProps={{ min: 0, step: '0.01' }}
                  InputProps={{ style: inputStyle }}
                  InputLabelProps={{ style: labelStyle }}
                  FormHelperTextProps={{ style: helperStyle }}
                />
              </Grid>

              {/* Image Location */}
              <Grid item xs={12}>
                <TextField
                  name="FrameImagelocation"
                  label="Frame Image Location"
                  value={formData.FrameImagelocation}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  disabled={isReadOnly}
                  placeholder="Enter image path or URL"
                  inputProps={{ maxLength: 200 }}
                  InputProps={{ style: { ...inputStyle, height: 'auto' } }}
                  InputLabelProps={{ style: labelStyle }}
                  FormHelperTextProps={{ style: helperStyle }}
                />
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions style={{
            padding: '8px 12px',
            background: '#f8f9fa',
            borderTop: '1px solid #e9ecef',
            gap: '6px',
            minHeight: 'auto'
          }}>
            <Button
              onClick={onClose}
              style={{
                color: '#6c757d', textTransform: 'none', fontWeight: '500',
                fontSize: '12px', padding: '4px 8px'
              }}
            >
              Cancel
            </Button>
            {!isReadOnly && (
              <Button
                onClick={handleSave}
                disabled={loading}
                variant="contained"
                style={{
                  background: 'linear-gradient(135deg, #c9a84c 0%, #9a7a2e 100%)',
                  color: 'white', textTransform: 'none', fontWeight: '600',
                  borderRadius: '4px', padding: '4px 12px', fontSize: '12px'
                }}
              >
                {loading ? 'Saving...' : mode === 'create' ? 'Add Frame' : 'Update Frame'}
              </Button>
            )}
          </DialogActions>
        </Box>
      </Dialog>

      {/* Duplicate Warning Dialog */}
      <Dialog
        open={duplicateDialogOpen}
        onClose={() => setDuplicateDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ style: { borderRadius: '12px', padding: '8px' } }}
      >
        <DialogContent style={{ textAlign: 'center', padding: '24px' }}>
          <WarningAmberIcon style={{ fontSize: '64px', color: '#ff9800', marginBottom: '16px' }} />
          <Typography variant="h6" style={{
            fontWeight: '600', marginBottom: '12px', color: '#2d3748', fontSize: '16px'
          }}>
            Duplicate Frame Found
          </Typography>
          <Typography style={{ color: '#666', fontSize: '14px', lineHeight: '1.5' }}>
            {duplicateMessage}
          </Typography>
        </DialogContent>
        <DialogActions style={{ padding: '0 24px 24px 24px', justifyContent: 'center' }}>
          <Button
            onClick={() => setDuplicateDialogOpen(false)}
            variant="contained"
            style={{
              background: 'linear-gradient(135deg, #5c3d1e 0%, #7a5230 100%)',
              color: 'white', textTransform: 'none', fontWeight: '600',
              borderRadius: '8px', padding: '8px 32px', fontSize: '14px'
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FrameModal;
